import React, { useRef, useState } from 'react'
import cn from 'classnames'
import {
    AudioLines,
    Globe,
    Image,
    Loader2,
    Lock,
    Music,
    Paintbrush,
    Film,
    Quote,
    SlidersHorizontal,
    Sparkles,
    Terminal,
    Video,
    Wrench,
    X,
} from 'lucide-react'
import AiMediaPreviewModal, {
    type AiMediaPreviewKind,
    type AiMediaPreviewTarget,
} from 'components/shared/AiMediaPreviewModal'
import { IAiModel } from 'state/aiModels'
import useStore from 'state/knovStore'
import { getDefaultAspectRatioForActionCommand } from 'utils/actionCommandMetadata'
import { endFrameRequiresStartFrame } from 'utils/aiVideoKeyframes'
import { postMediaActionPromptText } from 'components/answers/postMediaActions'
import styles from './AiActionPreview.module.scss'

type AiActionPreviewEditEvent =
    | React.MouseEvent<HTMLElement>
    | React.KeyboardEvent<HTMLElement>
    | React.TouchEvent<HTMLElement>

export interface AiActionPreviewProps {
    tag: string
    previewTitle?: string | null
    displayName?: string | null
    priceLabel?: string | null
    type?: 'model' | 'tool'
    modelType?: IAiModel['modelType'] | null
    defaultDurationSeconds?: number | null
    defaultAspectRatio?: string | null
    durationSeconds?: number | null
    aspectRatio?: string | null
    generationCount?: number | null
    remixKind?: string | null
    gifFrameRate?: number | null
    gifMaxWidth?: number | null
    screenshotTimestamp?: string | null
    extendActionTag?: string | null
    extendModelDisplayName?: string | null
    extendTimestamp?: string | null
    smartStitch?: boolean | null
    hasTextInput?: boolean
    textInputValue?: string | null
    textInputIsDefault?: boolean
    hasImageInput?: boolean
    imageInputUrl?: string | null
    referenceImageInputs?: Array<{
        url?: string | null
        label?: string | null
        signedId?: string | null
        signed_id?: string | null
        uploadId?: string | null
        upload_id?: string | null
        contentType?: string | null
        content_type?: string | null
    }> | null
    videoKeyframeMode?: boolean
    hasVideoInput?: boolean
    videoInputUrl?: string | null
    videoPreviewImageUrl?: string | null
    videoInputs?: Array<{
        url?: string | null
        label?: string | null
        previewImageUrl?: string | null
        preview_image_url?: string | null
        signedId?: string | null
        signed_id?: string | null
        uploadId?: string | null
        upload_id?: string | null
        contentType?: string | null
        content_type?: string | null
    }> | null
    referenceVideoInputs?: Array<{
        url?: string | null
        label?: string | null
        previewImageUrl?: string | null
        preview_image_url?: string | null
        contentType?: string | null
        content_type?: string | null
    }> | null
    hasAudioInput?: boolean
    audioInputUrl?: string | null
    audioInputLabel?: string | null
    audioInputContentType?: string | null
    referenceAudioInputs?: Array<{
        url?: string | null
        label?: string | null
        signedId?: string | null
        signed_id?: string | null
        uploadId?: string | null
        upload_id?: string | null
        contentType?: string | null
        content_type?: string | null
    }> | null
    hasLyrics?: boolean
    lyrics?: string | null
    inputOrder?: string[] | null
    supportsTextInput?: boolean
    supportsImageInput?: boolean
    supportsVideoInput?: boolean
    supportsAudioInput?: boolean
    supportsReferenceInputs?: boolean
    syncWithDraft?: boolean
    hasPendingUpload?: boolean
    hasFailedUpload?: boolean
    compact?: boolean
    fullWidth?: boolean
    simpleMode?: boolean
    simpleKind?: 'illustrate' | 'film' | null
    // The existing post a simple Illustrate/Film action was launched from (answer controls
    // menu). Rendered as its own row; clicking opens that post via onOpenSourcePost.
    sourcePostReference?: {
        answerId: string
        author?: string | null
        excerpt?: string | null
    } | null
    onOpenSourcePost?: (() => void) | null
    // Model · duration · resolution summary chip and settings entry point for simple cards.
    simpleSettingsSummary?: string | null
    onOpenSimpleSettings?: (() => void) | null
    generationErrorMessage?: string | null
    generationErrorRetryButton?: React.ReactNode
    missingInputKinds?: string[] | null
    // Owner-only sharing state of the generation metadata (prompt/settings/inputs).
    // When onToggleSharing is provided the card header renders a Public/Private
    // toggle; other viewers never receive these props.
    sharingPublic?: boolean | null
    onToggleSharing?: (event?: AiActionPreviewEditEvent) => void
    // Posted-answer popovers: filled input rows open read-only viewers (full
    // prompt/lyrics text, media previews) instead of edit dialogs.
    readOnlyInputs?: boolean
    onEdit?: (event?: AiActionPreviewEditEvent) => void
    onEditInput?: (
        inputKind: 'text' | 'lyrics' | 'image' | 'video' | 'audio',
        inputSlotIndex?: number,
        event?: AiActionPreviewEditEvent,
    ) => void
    onEditModel?: (event?: AiActionPreviewEditEvent) => void
    onEditExtendModel?: (event?: AiActionPreviewEditEvent) => void
    onEditReferences?: (event?: AiActionPreviewEditEvent) => void
    onRemove?: () => void
}

function modelTypeIcon(modelType: AiActionPreviewProps['modelType']) {
    switch (modelType) {
        case 'image':
            return Image
        case 'video':
            return Video
        case 'audio':
            return Music
        case 'text':
            return Terminal
        default:
            return Sparkles
    }
}

function previewRowOrder(tag: string, key: string) {
    const normalizedTag = tag.replace(/^!+/, '')

    if (['minimaxmusic', 'suno'].includes(normalizedTag)) {
        if (key === 'model') return 0
        if (key === 'text') return 0
        if (key === 'lyrics') return 1
        if (key === 'audio') return 2
        return 10
    }

    if (normalizedTag === 'extend') {
        if (key === 'model') return 0
        if (key === 'extend-model') return 0
        if (key === 'video' || key === 'videos') return 1
        if (key === 'text') return 2
        if (key === 'references') return 3
        return 10
    }

    if (key === 'model') return 0
    if (key === 'source-post') return 0
    if (key === 'text') return 0
    if (key === 'image') return 1
    if (key === 'video' || key === 'videos') return 2
    if (key === 'references') return 3
    if (key === 'audio') return 4
    if (key === 'lyrics') return 5
    return 10
}

const hasReferenceInputValue = (input?: {
    url?: string | null
    signedId?: string | null
    signed_id?: string | null
    uploadId?: string | null
    upload_id?: string | null
}) => !!input?.url

const TOUCH_SCROLL_ACTIVATION_THRESHOLD_PX = 8

export default function AiActionPreview({
    tag,
    previewTitle,
    displayName,
    priceLabel,
    type = 'tool',
    modelType,
    defaultDurationSeconds,
    defaultAspectRatio,
    durationSeconds,
    aspectRatio,
    generationCount,
    remixKind,
    gifFrameRate,
    gifMaxWidth,
    screenshotTimestamp,
    extendActionTag,
    extendModelDisplayName,
    extendTimestamp,
    smartStitch,
    hasTextInput,
    textInputValue,
    textInputIsDefault,
    hasImageInput,
    imageInputUrl,
    referenceImageInputs,
    videoKeyframeMode,
    hasVideoInput,
    videoInputUrl,
    videoPreviewImageUrl,
    videoInputs,
    referenceVideoInputs,
    hasAudioInput,
    audioInputUrl,
    audioInputLabel,
    audioInputContentType,
    referenceAudioInputs,
    hasLyrics,
    lyrics,
    supportsTextInput,
    supportsImageInput,
    supportsVideoInput,
    supportsAudioInput,
    supportsReferenceInputs,
    hasPendingUpload,
    hasFailedUpload,
    fullWidth,
    simpleMode,
    simpleKind,
    sourcePostReference,
    onOpenSourcePost,
    simpleSettingsSummary,
    onOpenSimpleSettings,
    generationErrorMessage,
    generationErrorRetryButton,
    missingInputKinds,
    sharingPublic,
    onToggleSharing,
    readOnlyInputs,
    onEdit,
    onEditInput,
    onEditModel,
    onEditExtendModel,
    onEditReferences,
    onRemove,
}: AiActionPreviewProps) {
    const [mediaPreviewTarget, setMediaPreviewTarget] = useState<AiMediaPreviewTarget | null>(null)
    const lastTouchActivationAtRef = useRef(0)
    const touchActivationRef = useRef<{
        startX: number
        startY: number
        moved: boolean
    } | null>(null)
    const normalizedTag = tag.replace(/^!+/, '')
    const isKeyframeMode =
        ['h3', 'h3max', 'kling', 'kling4'].includes(normalizedTag) &&
        (endFrameRequiresStartFrame(normalizedTag) || !!hasImageInput || videoKeyframeMode === true)
    const isAiAction = type === 'model'
    const supportsLyricsInput = ['minimaxmusic', 'suno'].includes(normalizedTag)
    const Icon = isAiAction ? modelTypeIcon(modelType) : Wrench
    const SimpleIcon = simpleKind === 'film' ? Film : Paintbrush
    const simpleLabel = simpleKind === 'film' ? 'Film' : 'Illustrate'
    const isRemixAction = remixKind === 'remix'
    const revealSimplePrompt = readOnlyInputs === true || isRemixAction
    const simplePromptText = postMediaActionPromptText({
        kind: simpleKind,
        prompt: textInputValue,
        revealGeneratedPrompt: revealSimplePrompt,
    })
    const mediaTitle =
        normalizedTag === 'gif'
            ? 'Create GIF'
            : modelType === 'image'
            ? 'Create Image'
            : modelType === 'video'
            ? 'Create Video'
            : modelType === 'audio'
            ? 'Create Audio'
            : modelType === 'text'
            ? 'Create Text'
            : 'Create Media'
    const title = simpleMode
        ? simpleLabel
        : normalizedTag === 'extend'
        ? 'Extend Video'
        : fullWidth && isAiAction
        ? previewTitle || mediaTitle
        : displayName || normalizedTag
    const resolvedPriceLabel = priceLabel || null
    const resolvedDefaultAspectRatio =
        defaultAspectRatio || getDefaultAspectRatioForActionCommand(normalizedTag)
    const modelPillLabel =
        normalizedTag === 'extend'
            ? extendModelDisplayName || (extendActionTag ? `!${extendActionTag}` : 'Choose Model')
            : displayName || normalizedTag
    const modelPillTag = normalizedTag
    const metadata = [
        generationCount && generationCount > 1 ? `x${generationCount}` : null,
        durationSeconds || defaultDurationSeconds
            ? `${durationSeconds || defaultDurationSeconds}s`
            : null,
        aspectRatio || resolvedDefaultAspectRatio
            ? `AR ${aspectRatio || resolvedDefaultAspectRatio}`
            : null,
        smartStitch ? 'Smart Stitch' : null,
        gifFrameRate ? `${gifFrameRate}fps` : null,
        gifMaxWidth ? `${gifMaxWidth}px` : null,
        screenshotTimestamp || extendTimestamp || null,
    ].filter(Boolean) as string[]
    const hasPriceLabel =
        typeof resolvedPriceLabel === 'string' && resolvedPriceLabel.trim().length > 0
    const missingInputKindSet = new Set(missingInputKinds || [])

    const recordTouchStart = (event: React.TouchEvent<HTMLElement>) => {
        const touch = event.touches[0]
        touchActivationRef.current = touch
            ? {
                  startX: touch.clientX,
                  startY: touch.clientY,
                  moved: false,
              }
            : null
    }

    const recordTouchMove = (event: React.TouchEvent<HTMLElement>) => {
        const gesture = touchActivationRef.current
        const touch = event.touches[0]
        if (!gesture || !touch) return

        const movedDistance = Math.hypot(
            touch.clientX - gesture.startX,
            touch.clientY - gesture.startY,
        )
        if (movedDistance > TOUCH_SCROLL_ACTIVATION_THRESHOLD_PX) {
            gesture.moved = true
        }
    }

    const consumeTouchScrollEnd = (event: React.TouchEvent<HTMLElement>) => {
        const gesture = touchActivationRef.current
        touchActivationRef.current = null
        if (!gesture) return false

        const touch = event.changedTouches[0]
        const movedDistance = touch
            ? Math.hypot(touch.clientX - gesture.startX, touch.clientY - gesture.startY)
            : 0
        const wasScrollGesture =
            gesture.moved || movedDistance > TOUCH_SCROLL_ACTIVATION_THRESHOLD_PX

        if (!wasScrollGesture) return false

        event.preventDefault()
        event.stopPropagation()
        lastTouchActivationAtRef.current = Date.now()
        return true
    }

    const ignoreSyntheticClickAfterTouch = (event: React.SyntheticEvent) => {
        if (Date.now() - lastTouchActivationAtRef.current >= 700) return false

        event.preventDefault()
        event.stopPropagation()
        return true
    }

    const openMediaPreview = (
        kind: AiMediaPreviewKind,
        input: {
            url?: string | null
            label?: string | null
            previewImageUrl?: string | null
            preview_image_url?: string | null
            contentType?: string | null
            content_type?: string | null
            text?: string | null
        },
    ) => {
        if (kind === 'text' ? !input.text : !input.url) return
        setMediaPreviewTarget({
            kind,
            input: {
                url: input.url || null,
                label: input.label || null,
                previewImageUrl: input.previewImageUrl || input.preview_image_url || null,
                contentType: input.contentType || input.content_type || null,
                text: input.text || null,
            },
        })
    }

    const mediaPreviewButtonProps = (
        kind: AiMediaPreviewKind,
        input: {
            url?: string | null
            label?: string | null
            previewImageUrl?: string | null
            preview_image_url?: string | null
            contentType?: string | null
            content_type?: string | null
        },
    ) => ({
        'aria-label': `Preview ${input.label || kind}`,
        onClick: (event: React.MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            if (ignoreSyntheticClickAfterTouch(event)) return
            openMediaPreview(kind, input)
        },
        onMouseDown: (event: React.MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
        },
        onTouchStart: recordTouchStart,
        onTouchMove: recordTouchMove,
        onTouchEnd: (event: React.TouchEvent<HTMLElement>) => {
            if (consumeTouchScrollEnd(event)) return
            event.preventDefault()
            event.stopPropagation()
            lastTouchActivationAtRef.current = Date.now()
            openMediaPreview(kind, input)
        },
        type: 'button' as const,
    })

    const shouldIgnoreRowActivation = (event: React.SyntheticEvent<HTMLElement>) => {
        const target = event.target
        if (!(target instanceof HTMLElement)) return false

        const nestedInteractive = target.closest(
            'button,a,input,textarea,select,[contenteditable="true"],[data-ai-action-preview-stop-row-activation]',
        )

        return (
            nestedInteractive instanceof HTMLElement &&
            nestedInteractive !== event.currentTarget &&
            event.currentTarget.contains(nestedInteractive)
        )
    }

    const renderRemoveButton = () =>
        onRemove ? (
            <button
                aria-label={`Remove ${normalizedTag} action token`}
                className={styles.removeButton}
                onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    useStore
                        .getState()
                        .showGlobalNotice('Confirm you want to remove this AI action?', {
                            variant: 'info',
                            durationMs: 10000,
                            kind: 'ai-action-remove-confirm',
                            testId: 'toast-ai-action-remove-confirm',
                            actionLabel: 'Remove',
                            cancelLabel: 'Cancel',
                            onAction: onRemove,
                        })
                }}
                onMouseDown={event => {
                    event.preventDefault()
                    event.stopPropagation()
                }}
                tabIndex={-1}
                type="button"
            >
                <X size={14} strokeWidth={2.5} />
            </button>
        ) : null

    const renderSharingToggle = () =>
        onToggleSharing ? (
            <button
                aria-label={
                    sharingPublic
                        ? 'Generation details are public. Make them private'
                        : 'Generation details are private. Share them publicly'
                }
                aria-pressed={sharingPublic === true}
                className={cn(styles.sharingToggle, sharingPublic && styles.sharingTogglePublic)}
                onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    if (ignoreSyntheticClickAfterTouch(event)) return
                    onToggleSharing(event)
                }}
                onMouseDown={event => {
                    event.preventDefault()
                    event.stopPropagation()
                }}
                onTouchStart={recordTouchStart}
                onTouchMove={recordTouchMove}
                onTouchEnd={event => {
                    if (consumeTouchScrollEnd(event)) return
                    event.preventDefault()
                    event.stopPropagation()
                    lastTouchActivationAtRef.current = Date.now()
                    onToggleSharing(event)
                }}
                title={
                    sharingPublic
                        ? 'Everyone can see this generation card. Tap to keep it private.'
                        : 'Only you can see this generation card. Tap to share it publicly.'
                }
                type="button"
            >
                {sharingPublic ? (
                    <Globe size={11} strokeWidth={2.6} />
                ) : (
                    <Lock size={11} strokeWidth={2.6} />
                )}
                <span>{sharingPublic ? 'Public' : 'Private'}</span>
            </button>
        ) : null

    const renderMetadata = ({ includePrice = false, fullWidth = false } = {}) => {
        const showMetadata = isAiAction && (metadata.length > 0 || (includePrice && hasPriceLabel))
        if (!showMetadata) return null

        return (
            <span className={cn(styles.metadata, fullWidth && styles.fullWidthMetadata)}>
                {includePrice && hasPriceLabel && (
                    <span className={cn(styles.priceLabel, styles.metadataPriceLabel)}>
                        {resolvedPriceLabel}
                    </span>
                )}
                {metadata.map(item => (
                    <span className={styles.metadataChip} key={item}>
                        {item}
                    </span>
                ))}
            </span>
        )
    }

    const renderCompactInputs = () =>
        isAiAction ? (
            <span className={styles.inputPreviewGroup} aria-hidden="true">
                {hasTextInput && (
                    <span
                        className={cn(
                            styles.inputPreview,
                            styles.inputPreviewText,
                            textInputIsDefault && styles.inputPreviewDefaultText,
                        )}
                    >
                        <Terminal size={10} />
                        {textInputValue && <span>{textInputValue}</span>}
                    </span>
                )}
                {hasImageInput && (
                    <span className={styles.inputPreview}>
                        {imageInputUrl ? <img src={imageInputUrl} alt="" /> : <Image size={10} />}
                    </span>
                )}
                {hasVideoInput && (
                    <span className={styles.inputPreview}>
                        {videoPreviewImageUrl ? (
                            <img src={videoPreviewImageUrl} alt="" />
                        ) : (
                            <Video size={10} />
                        )}
                    </span>
                )}
                {hasAudioInput && (
                    <span className={cn(styles.inputPreview, styles.inputPreviewIcon)}>
                        <AudioLines size={10} />
                    </span>
                )}
                {hasLyrics && (
                    <span className={cn(styles.inputPreview, styles.inputPreviewIcon)}>
                        <span className={styles.inputPreviewLyricsGlyph}>L</span>
                    </span>
                )}
                {(hasPendingUpload || hasFailedUpload) && (
                    <span className={cn(styles.inputPreview, styles.inputPreviewIcon)}>
                        {hasFailedUpload ? (
                            <X size={10} />
                        ) : (
                            <Loader2 className={styles.uploadSpinner} size={10} />
                        )}
                    </span>
                )}
            </span>
        ) : null

    const renderFullWidthInputs = () => {
        if (!isAiAction && !simpleMode) return null
        const referenceRows = [
            {
                key: 'reference-images',
                icon: <Image size={11} />,
                className: styles.inputPreviewRowImage,
                inputs: (referenceImageInputs || []).filter(hasReferenceInputValue),
                renderInput: (
                    input: { url?: string | null; label?: string | null },
                    index: number,
                ) => (
                    <button
                        className={styles.inputPreviewReferenceItem}
                        key={input.url || index}
                        {...mediaPreviewButtonProps('image', input)}
                    >
                        {input.url ? <img src={input.url} alt="" /> : <Image size={11} />}
                    </button>
                ),
            },
            {
                key: 'reference-videos',
                icon: <Video size={11} />,
                inputs: (referenceVideoInputs || []).filter(hasReferenceInputValue),
                renderInput: (
                    input: {
                        url?: string | null
                        label?: string | null
                        previewImageUrl?: string | null
                        preview_image_url?: string | null
                    },
                    index: number,
                ) => (
                    <button
                        className={styles.inputPreviewReferenceItem}
                        key={input.url || index}
                        {...mediaPreviewButtonProps('video', input)}
                    >
                        {input.previewImageUrl || input.preview_image_url ? (
                            <img
                                src={input.previewImageUrl || input.preview_image_url || ''}
                                alt=""
                            />
                        ) : (
                            <span className={styles.inputPreviewReferenceFallback} />
                        )}
                    </button>
                ),
            },
            {
                key: 'reference-audios',
                icon: <AudioLines size={11} />,
                className: styles.inputPreviewRowAudio,
                inputs: (referenceAudioInputs || []).filter(hasReferenceInputValue),
                renderInput: (
                    input: { url?: string | null; label?: string | null },
                    index: number,
                ) => (
                    <button
                        className={styles.inputPreviewReferenceItem}
                        key={input.url || index}
                        {...mediaPreviewButtonProps('audio', input)}
                    >
                        <span className={styles.inputPreviewAudioBlock}>
                            <AudioLines size={18} />
                        </span>
                    </button>
                ),
            },
        ].filter(row => row.inputs.length > 0)

        const videoInputRows = (videoInputs || []).filter(input => !!input?.url)
        const stitchVideoSlots =
            normalizedTag === 'stitch' ? [videoInputs?.[0] || null, videoInputs?.[1] || null] : []
        const shouldShowReferenceRow = supportsReferenceInputs || referenceRows.length > 0

        const unsortedRows = [
            {
                key: 'model',
                label: 'Model',
                icon: <Icon size={11} />,
                content: (
                    <span className={styles.modelPillContent}>
                        <span className={styles.modelPillMainLine}>
                            <span
                                className={cn(
                                    styles.modelPillName,
                                    normalizedTag === 'extend' &&
                                        !extendActionTag &&
                                        styles.inputPreviewPlaceholderText,
                                )}
                            >
                                {modelPillLabel}
                            </span>
                            {modelPillTag && (
                                <span className={styles.modelPillTag}>!{modelPillTag}</span>
                            )}
                        </span>
                        {metadata.length > 0 && (
                            <span className={styles.modelPillMetaLine}>
                                {metadata.map(item => (
                                    <span className={styles.modelPillMetaItem} key={item}>
                                        {item}
                                    </span>
                                ))}
                            </span>
                        )}
                    </span>
                ),
                className: styles.inputPreviewRowModel,
                hideLabel: true,
                inputKind: 'model' as const,
                hasValue:
                    normalizedTag !== 'extend' || !!extendActionTag || !!extendModelDisplayName,
            },
            sourcePostReference
                ? {
                      key: 'source-post',
                      label: '',
                      icon: <Quote size={11} />,
                      inputKind: 'text' as const,
                      content: (
                          <span className={styles.inputPreviewRowTextContent}>
                              {`${simpleKind === 'film' ? 'Filming' : 'Illustrating'} ${
                                  sourcePostReference.author
                                      ? `${sourcePostReference.author}'s post`
                                      : 'the referenced post'
                              }${
                                  sourcePostReference.excerpt
                                      ? ` — “${sourcePostReference.excerpt}”`
                                      : ''
                              }`}
                          </span>
                      ),
                      className: cn(styles.inputPreviewRowText, styles.inputPreviewRowSourcePost),
                      hideLabel: true,
                      hasValue: true,
                  }
                : null,
            simpleMode || supportsTextInput || hasTextInput
                ? {
                      key: 'text',
                      label: '',
                      icon: <Terminal size={11} />,
                      inputKind: 'text' as const,
                      content: (
                          <span
                              className={cn(
                                  styles.inputPreviewRowTextContent,
                                  !simpleMode &&
                                      textInputIsDefault &&
                                      styles.inputPreviewDefaultText,
                                  !simpleMode &&
                                      !hasTextInput &&
                                      styles.inputPreviewPlaceholderText,
                                  simpleMode && styles.inputPreviewDisabledText,
                              )}
                          >
                              {simpleMode
                                  ? simplePromptText
                                  : hasTextInput
                                  ? textInputValue || 'Text input'
                                  : 'Add Prompt'}
                          </span>
                      ),
                      className: cn(styles.inputPreviewRowText, styles.inputPreviewRowPrompt),
                      hideLabel: true,
                      hasValue: simpleMode || !!hasTextInput,
                      readOnlyPreview:
                          (!simpleMode || revealSimplePrompt) && hasTextInput && textInputValue
                              ? {
                                    kind: 'text' as const,
                                    input: { label: 'Prompt', text: textInputValue },
                                }
                              : null,
                  }
                : null,
            normalizedTag !== 'extend' && (supportsImageInput || hasImageInput)
                ? {
                      key: 'image',
                      label: isKeyframeMode ? 'Start Frame' : 'Image',
                      icon: <Image size={11} />,
                      inputKind: 'image' as const,
                      content: imageInputUrl ? (
                          <button
                              className={styles.inputPreviewMediaButton}
                              {...mediaPreviewButtonProps('image', {
                                  url: imageInputUrl,
                                  label: isKeyframeMode ? 'Start frame' : 'Image input',
                              })}
                          >
                              <img src={imageInputUrl} alt="" />
                          </button>
                      ) : hasImageInput && (hasPendingUpload || hasFailedUpload) ? (
                          <span
                              className={cn(
                                  styles.inputPreviewPendingText,
                                  hasFailedUpload && styles.inputPreviewFailedText,
                              )}
                          >
                              {hasFailedUpload ? 'Upload Failed' : 'Uploading Image'}
                          </span>
                      ) : (
                          <span className={styles.inputPreviewPlaceholderText}>
                              {isKeyframeMode ? 'Add Start Frame' : 'Add Image'}
                          </span>
                      ),
                      className: styles.inputPreviewRowImage,
                      hideLabel: true,
                      hasValue: !!hasImageInput,
                      readOnlyPreview: imageInputUrl
                          ? {
                                kind: 'image' as const,
                                input: {
                                    url: imageInputUrl,
                                    label: isKeyframeMode ? 'Start frame' : 'Image input',
                                },
                            }
                          : null,
                  }
                : null,
            ...stitchVideoSlots.map((input, index) => ({
                key: `video-${index}`,
                label: `Video ${index + 1}`,
                icon: <Video size={11} />,
                inputKind: 'video' as const,
                inputSlotIndex: index,
                readOnlyPreview: input?.url
                    ? {
                          kind: 'video' as const,
                          input: {
                              url: input.url,
                              label: input.label || `Video ${index + 1}`,
                              previewImageUrl:
                                  input.previewImageUrl || input.preview_image_url || null,
                              contentType: input.contentType || input.content_type || null,
                          },
                      }
                    : null,
                content:
                    input?.previewImageUrl || input?.preview_image_url ? (
                        <button
                            className={styles.inputPreviewMediaButton}
                            {...mediaPreviewButtonProps('video', {
                                url: input.url,
                                label: input.label || `Video ${index + 1}`,
                                previewImageUrl: input.previewImageUrl || input.preview_image_url,
                                contentType: input.contentType || input.content_type,
                            })}
                        >
                            <img
                                src={input.previewImageUrl || input.preview_image_url || ''}
                                alt=""
                            />
                        </button>
                    ) : input?.url ? (
                        <button
                            className={styles.inputPreviewMediaButton}
                            {...mediaPreviewButtonProps('video', {
                                url: input.url,
                                label: input.label || `Video ${index + 1}`,
                                contentType: input.contentType || input.content_type,
                            })}
                        >
                            <video src={input.url} muted playsInline preload="metadata" />
                        </button>
                    ) : (
                        <span className={styles.inputPreviewPlaceholderText}>
                            Add Video {index + 1}
                        </span>
                    ),
                className: styles.inputPreviewRowVideo,
                hideLabel: true,
                hasValue: !!input?.url,
            })),
            supportsVideoInput || hasVideoInput
                ? {
                      key: 'video',
                      label: 'Video',
                      icon: <Video size={11} />,
                      inputKind: 'video' as const,
                      content: videoPreviewImageUrl ? (
                          <button
                              className={styles.inputPreviewMediaButton}
                              {...mediaPreviewButtonProps('video', {
                                  url: videoInputUrl,
                                  label: 'Video input',
                                  previewImageUrl: videoPreviewImageUrl,
                              })}
                          >
                              <img src={videoPreviewImageUrl} alt="" />
                          </button>
                      ) : hasVideoInput && videoInputUrl ? (
                          <button
                              className={styles.inputPreviewMediaButton}
                              {...mediaPreviewButtonProps('video', {
                                  url: videoInputUrl,
                                  label: 'Video input',
                              })}
                          >
                              <video src={videoInputUrl} muted playsInline preload="metadata" />
                          </button>
                      ) : hasVideoInput && (hasPendingUpload || hasFailedUpload) ? (
                          <span
                              className={cn(
                                  styles.inputPreviewPendingText,
                                  hasFailedUpload && styles.inputPreviewFailedText,
                              )}
                          >
                              {hasFailedUpload ? 'Upload Failed' : 'Uploading Video'}
                          </span>
                      ) : (
                          <span className={styles.inputPreviewPlaceholderText}>Add Video</span>
                      ),
                      className: styles.inputPreviewRowVideo,
                      hideLabel: true,
                      hasValue: !!hasVideoInput,
                      readOnlyPreview: videoInputUrl
                          ? {
                                kind: 'video' as const,
                                input: {
                                    url: videoInputUrl,
                                    label: 'Video input',
                                    previewImageUrl: videoPreviewImageUrl || null,
                                },
                            }
                          : null,
                  }
                : null,
            videoInputRows.length > 0
                ? {
                      key: 'videos',
                      label: '',
                      icon: <Video size={11} />,
                      content: (
                          <span className={styles.inputPreviewReferenceList}>
                              {videoInputRows.map((input, index) => (
                                  <button
                                      className={styles.inputPreviewReferenceItem}
                                      key={input.url || index}
                                      {...mediaPreviewButtonProps('video', {
                                          url: input.url,
                                          label: input.label || `Video ${index + 1}`,
                                          previewImageUrl:
                                              input.previewImageUrl || input.preview_image_url,
                                          contentType: input.contentType || input.content_type,
                                      })}
                                  >
                                      {input.previewImageUrl || input.preview_image_url ? (
                                          <img
                                              src={
                                                  input.previewImageUrl ||
                                                  input.preview_image_url ||
                                                  ''
                                              }
                                              alt=""
                                          />
                                      ) : (
                                          <Video size={11} />
                                      )}
                                  </button>
                              ))}
                          </span>
                      ),
                      className: cn(styles.inputPreviewRowReferences, styles.inputPreviewRowVideo),
                      hideLabel: true,
                      hasValue: true,
                  }
                : null,
            normalizedTag !== 'extend' && (supportsAudioInput || hasAudioInput)
                ? {
                      key: 'audio',
                      label: 'Audio',
                      icon: <AudioLines size={11} />,
                      inputKind: 'audio' as const,
                      content: hasAudioInput ? (
                          <span className={styles.inputPreviewRowTextContent}>
                              {audioInputUrl && (
                                  <button
                                      className={cn(
                                          styles.inputPreviewMediaButton,
                                          styles.inputPreviewAudioButton,
                                      )}
                                      {...mediaPreviewButtonProps('audio', {
                                          url: audioInputUrl,
                                          label: audioInputLabel || 'Audio input',
                                          contentType: audioInputContentType,
                                      })}
                                  >
                                      <AudioLines size={18} />
                                  </button>
                              )}
                              {audioInputLabel ||
                                  (hasFailedUpload
                                      ? 'Upload Failed'
                                      : hasPendingUpload
                                      ? 'Uploading Audio'
                                      : 'Audio')}
                          </span>
                      ) : (
                          <span className={styles.inputPreviewPlaceholderText}>Add Audio</span>
                      ),
                      className: styles.inputPreviewRowAudio,
                      hideLabel: true,
                      hasValue: !!hasAudioInput,
                      readOnlyPreview: audioInputUrl
                          ? {
                                kind: 'audio' as const,
                                input: {
                                    url: audioInputUrl,
                                    label: audioInputLabel || 'Audio input',
                                    contentType: audioInputContentType || null,
                                },
                            }
                          : null,
                  }
                : null,
            supportsLyricsInput || hasLyrics
                ? {
                      key: 'lyrics',
                      label: '',
                      icon: <span className={styles.inputPreviewRowGlyph}>L</span>,
                      inputKind: 'lyrics' as const,
                      content: (
                          <span className={styles.inputPreviewRowTextContent}>
                              {lyrics?.trim() || (hasLyrics ? 'Lyrics' : 'Add Lyrics')}
                          </span>
                      ),
                      className: styles.inputPreviewRowText,
                      hideLabel: true,
                      hasValue: !!hasLyrics,
                      readOnlyPreview: lyrics?.trim()
                          ? {
                                kind: 'text' as const,
                                input: { label: 'Lyrics', text: lyrics },
                            }
                          : null,
                  }
                : null,
            shouldShowReferenceRow
                ? {
                      key: 'references',
                      label: '',
                      icon: (
                          <span className={styles.inputPreviewRowGlyph}>
                              {isKeyframeMode ? 'E' : 'R'}
                          </span>
                      ),
                      content:
                          referenceRows.length > 0 ? (
                              <span className={styles.inputPreviewReferencePill}>
                                  <span className={styles.inputPreviewReferenceHeader}>
                                      <span className={styles.inputPreviewRowGlyph}>
                                          {isKeyframeMode ? 'E' : 'R'}
                                      </span>
                                      <span>{isKeyframeMode ? 'End Frame' : 'References'}</span>
                                  </span>
                                  <span className={styles.inputPreviewReferenceMediaRows}>
                                      {referenceRows.map(row => (
                                          <span
                                              className={styles.inputPreviewReferenceMediaRow}
                                              key={row.key}
                                          >
                                              <span
                                                  className={styles.inputPreviewReferenceMediaIcon}
                                              >
                                                  {row.icon}
                                              </span>
                                              <span className={styles.inputPreviewReferenceList}>
                                                  {row.inputs.map(row.renderInput)}
                                              </span>
                                          </span>
                                      ))}
                                  </span>
                              </span>
                          ) : (
                              <span
                                  className={cn(
                                      styles.inputPreviewPlaceholderText,
                                      styles.inputPreviewReferencesPlaceholder,
                                  )}
                              >
                                  <span className={styles.inputPreviewRowGlyph}>
                                      {isKeyframeMode ? 'E' : 'R'}
                                  </span>
                                  {isKeyframeMode ? 'Add End Frame' : 'Add References'}
                              </span>
                          ),
                      className: styles.inputPreviewRowReferences,
                      hideLabel: true,
                      isReference: true,
                      inputKind: 'references' as const,
                      hasValue: referenceRows.length > 0,
                  }
                : null,
        ].filter(Boolean) as Array<{
            key: string
            label: string
            icon: React.ReactNode
            content: React.ReactNode
            className?: string
            hideLabel?: boolean
            inputKind?:
                | 'model'
                | 'text'
                | 'lyrics'
                | 'image'
                | 'video'
                | 'audio'
                | 'references'
                | 'extend-model'
            isReference?: boolean
            hasValue?: boolean
            inputSlotIndex?: number
            readOnlyPreview?: {
                kind: AiMediaPreviewKind
                input: {
                    url?: string | null
                    label?: string | null
                    previewImageUrl?: string | null
                    contentType?: string | null
                    text?: string | null
                }
            } | null
        }>
        const visibleRows = simpleMode
            ? unsortedRows.filter(
                  row => row.key !== 'model' && (row.key === 'text' || row.hasValue === true),
              )
            : unsortedRows
        const rows = [...visibleRows].sort((left, right) => {
            const orderDiff = previewRowOrder(tag, left.key) - previewRowOrder(tag, right.key)
            if (orderDiff !== 0) return orderDiff
            return unsortedRows.indexOf(left) - unsortedRows.indexOf(right)
        })

        if (rows.length === 0) return null

        return (
            <span className={styles.inputPreviewRows}>
                {rows.map(row => {
                    const isMissingRequired =
                        missingInputKindSet.has(row.key) ||
                        (!!row.inputKind && missingInputKindSet.has(row.inputKind))
                    const canEditRow = simpleMode
                        ? false
                        : row.inputKind === 'model'
                        ? normalizedTag === 'extend'
                            ? !!onEditExtendModel
                            : !!onEditModel
                        : row.inputKind === 'references'
                        ? !!onEditReferences
                        : row.inputKind === 'extend-model'
                        ? !!onEditExtendModel
                        : !!row.inputKind && !!onEditInput
                    // Filled rows in read-only cards open viewers (full prompt or
                    // lyrics text, media previews) instead of edit dialogs.
                    const canViewRow =
                        !canEditRow && readOnlyInputs === true && !!row.readOnlyPreview
                    // Simple cards launched from an existing post get a dedicated source row
                    // that opens that post without replacing the generated prompt row.
                    const canOpenSourceRow =
                        simpleMode &&
                        row.key === 'source-post' &&
                        !!sourcePostReference &&
                        !!onOpenSourcePost
                    const canActivateRow = canEditRow || canViewRow || canOpenSourceRow
                    const activateRow = (event: AiActionPreviewEditEvent) => {
                        if (!row.inputKind || !canActivateRow) return
                        event.preventDefault()
                        event.stopPropagation()
                        if (canOpenSourceRow) {
                            onOpenSourcePost?.()
                            return
                        }
                        if (canViewRow && row.readOnlyPreview) {
                            openMediaPreview(row.readOnlyPreview.kind, row.readOnlyPreview.input)
                            return
                        }
                        if (row.inputKind === 'model') {
                            if (normalizedTag === 'extend') {
                                onEditExtendModel?.(event)
                                return
                            }
                            onEditModel?.(event)
                            return
                        }
                        if (row.inputKind === 'extend-model') {
                            onEditExtendModel?.(event)
                            return
                        }
                        if (row.inputKind === 'references') {
                            onEditReferences?.(event)
                            return
                        }
                        if (!onEditInput) return
                        onEditInput(row.inputKind, row.inputSlotIndex, event)
                    }

                    const rowElement = (
                        <span
                            className={cn(
                                styles.inputPreviewRow,
                                canActivateRow && styles.inputPreviewRowClickable,
                                row.hasValue && styles.inputPreviewRowFilled,
                                !row.hasValue && styles.inputPreviewRowEmpty,
                                isMissingRequired && styles.inputPreviewRowRequired,
                                row.className,
                            )}
                            key={row.key}
                            aria-invalid={isMissingRequired || undefined}
                            data-action-input-kind={row.inputKind || undefined}
                            data-testid={
                                row.key === 'source-post'
                                    ? 'ai-action-source-post'
                                    : row.inputKind
                                    ? `ai-action-input-${row.inputKind}`
                                    : undefined
                            }
                            onClick={event => {
                                if (shouldIgnoreRowActivation(event)) return
                                if (ignoreSyntheticClickAfterTouch(event)) return
                                activateRow(event)
                            }}
                            onMouseDown={event => {
                                if (!row.inputKind || !canActivateRow) return
                                event.preventDefault()
                                event.stopPropagation()
                            }}
                            onTouchStart={recordTouchStart}
                            onTouchMove={recordTouchMove}
                            onTouchEnd={event => {
                                if (shouldIgnoreRowActivation(event)) {
                                    touchActivationRef.current = null
                                    return
                                }
                                if (consumeTouchScrollEnd(event)) return
                                if (!row.inputKind || !canActivateRow) return
                                lastTouchActivationAtRef.current = Date.now()
                                activateRow(event)
                            }}
                            onKeyDown={event => {
                                if (
                                    !row.inputKind ||
                                    !canActivateRow ||
                                    (event.key !== 'Enter' && event.key !== ' ')
                                )
                                    return
                                event.preventDefault()
                                event.stopPropagation()
                                activateRow(event)
                            }}
                            role={canActivateRow ? 'button' : undefined}
                            tabIndex={canActivateRow ? 0 : undefined}
                        >
                            {!row.hideLabel && (
                                <span className={styles.inputPreviewRowLabel}>
                                    {row.icon}
                                    <span>{row.label}</span>
                                </span>
                            )}
                            <span className={styles.inputPreviewRowValue}>
                                {row.hideLabel && !row.isReference && row.icon}
                                {row.content || row.icon}
                            </span>
                        </span>
                    )

                    // Simple Film/Illustrate cards keep the settings gear on the prompt row,
                    // mirroring the model row's control layout.
                    if (simpleMode && row.key === 'text' && onOpenSimpleSettings) {
                        return (
                            <span className={styles.modelControlRow} key={row.key}>
                                {rowElement}
                                <button
                                    aria-label="Edit settings"
                                    data-testid="post-media-simple-settings-button"
                                    className={styles.modelSettingsButton}
                                    onClick={event => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        if (ignoreSyntheticClickAfterTouch(event)) return
                                        onOpenSimpleSettings()
                                    }}
                                    onMouseDown={event => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                    }}
                                    onTouchStart={recordTouchStart}
                                    onTouchMove={recordTouchMove}
                                    onTouchEnd={event => {
                                        if (consumeTouchScrollEnd(event)) return
                                        event.preventDefault()
                                        event.stopPropagation()
                                        lastTouchActivationAtRef.current = Date.now()
                                        onOpenSimpleSettings()
                                    }}
                                    type="button"
                                >
                                    <SlidersHorizontal size={18} strokeWidth={2.4} />
                                </button>
                            </span>
                        )
                    }

                    if (row.inputKind === 'model' && onEdit) {
                        return (
                            <span className={styles.modelControlRow} key={row.key}>
                                {rowElement}
                                <button
                                    aria-label={`Configure ${normalizedTag} AI action`}
                                    className={styles.modelSettingsButton}
                                    onClick={event => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        if (ignoreSyntheticClickAfterTouch(event)) return
                                        onEdit(event)
                                    }}
                                    onMouseDown={event => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                    }}
                                    onTouchStart={recordTouchStart}
                                    onTouchMove={recordTouchMove}
                                    onTouchEnd={event => {
                                        if (consumeTouchScrollEnd(event)) return
                                        event.preventDefault()
                                        event.stopPropagation()
                                        lastTouchActivationAtRef.current = Date.now()
                                        onEdit(event)
                                    }}
                                    type="button"
                                >
                                    <SlidersHorizontal size={18} strokeWidth={2.4} />
                                </button>
                            </span>
                        )
                    }

                    return rowElement
                })}
            </span>
        )
    }

    if (fullWidth) {
        return (
            <div
                className={cn(
                    isAiAction ? styles.aiActionBadge : styles.toolActionBadge,
                    styles.fullWidthActionBadge,
                )}
                data-action-tag={normalizedTag}
                data-action-tag-type={type}
                data-action-tag-model-type={modelType || undefined}
                data-testid={simpleMode ? 'post-media-simple-action' : undefined}
                data-post-media-simple-kind={simpleMode ? simpleKind || undefined : undefined}
                onTouchStart={recordTouchStart}
                onTouchMove={recordTouchMove}
                onTouchEnd={event => {
                    consumeTouchScrollEnd(event)
                }}
            >
                <div className={styles.aiEditorPreviewMain}>
                    <div className={styles.aiEditorPreviewTopRow}>
                        <div className={styles.aiEditorPreviewContentButton}>
                            <span className={styles.aiEditorPreviewText}>
                                <span className={styles.headerTitle}>
                                    {simpleMode && <SimpleIcon aria-hidden="true" size={20} />}
                                    <span className={styles.headerTitleText}>{title}</span>
                                    {isRemixAction && (
                                        <span className={styles.headerRemixBadge}>Remix</span>
                                    )}
                                    {simpleMode && simpleSettingsSummary && (
                                        <span className={styles.headerPrice}>
                                            {simpleSettingsSummary}
                                        </span>
                                    )}
                                    {!simpleMode && hasPriceLabel && (
                                        <span className={styles.headerPrice}>
                                            {resolvedPriceLabel}
                                        </span>
                                    )}
                                </span>
                            </span>
                        </div>
                        <span className={styles.headerActions}>
                            {renderSharingToggle()}
                            {(hasPendingUpload || hasFailedUpload) && (
                                <span
                                    className={cn(
                                        styles.uploadStatus,
                                        hasFailedUpload && styles.uploadStatusFailed,
                                    )}
                                >
                                    {hasFailedUpload ? (
                                        <X size={13} />
                                    ) : (
                                        <Loader2 className={styles.uploadSpinner} size={13} />
                                    )}
                                    {hasFailedUpload ? 'Upload failed' : 'Uploading'}
                                </span>
                            )}
                            {renderRemoveButton()}
                        </span>
                    </div>
                    {generationErrorMessage && (
                        <div className={styles.generationErrorMessage}>
                            <div className={styles.generationErrorMessageHeader}>Error</div>
                            <div>{generationErrorMessage}</div>
                        </div>
                    )}
                    {renderFullWidthInputs()}
                    {generationErrorRetryButton && (
                        <div className={styles.generationErrorActions}>
                            {generationErrorRetryButton}
                        </div>
                    )}
                </div>
                <AiMediaPreviewModal
                    target={mediaPreviewTarget}
                    footerMode="done"
                    onClose={() => setMediaPreviewTarget(null)}
                />
            </div>
        )
    }

    return (
        <span
            className={cn(
                styles.actionBadge,
                isAiAction ? styles.aiActionBadge : styles.toolActionBadge,
            )}
            data-action-tag={normalizedTag}
            data-action-tag-type={type}
            data-action-tag-model-type={modelType || undefined}
            onClick={event => {
                if (!onEdit) return
                event.preventDefault()
                event.stopPropagation()
                if (ignoreSyntheticClickAfterTouch(event)) return
                onEdit()
            }}
            onMouseDown={event => {
                if (!onEdit) return
                event.preventDefault()
                event.stopPropagation()
            }}
            onKeyDown={event => {
                if (!onEdit || (event.key !== 'Enter' && event.key !== ' ')) return
                event.preventDefault()
                event.stopPropagation()
                onEdit()
            }}
            onTouchStart={recordTouchStart}
            onTouchMove={recordTouchMove}
            onTouchEnd={event => {
                consumeTouchScrollEnd(event)
            }}
            role={onEdit ? 'button' : undefined}
            tabIndex={onEdit ? 0 : undefined}
        >
            <span className={styles.tagMain}>
                <Icon className={styles.icon} size={13} strokeWidth={2.3} />
                <span className={styles.tagText}>!{normalizedTag}</span>
                {renderRemoveButton()}
            </span>
            {renderCompactInputs()}
            {renderMetadata()}
            <AiMediaPreviewModal
                target={mediaPreviewTarget}
                footerMode="done"
                onClose={() => setMediaPreviewTarget(null)}
            />
        </span>
    )
}
